// SPDX-License-Identifier: GPL-3.0-or-later
// Turns player input into feedback events: a flipper goes up or down, the plunger charges.
// The engine sends the rules a flipper press only, and no release, so the input is the place to
// see both. The bridge watches the inputs that the page sends to the engine. It never sends one.
//
// The plunger launch is not here. The rules emit it when the ball leaves the shooter lane, so the
// sound and the effect match the real launch. A page that wants a charge meter can emit
// `plunger_charge` again at each frame with the pull from the power gauge.

import type { InputEvent } from "../../shared/contracts.ts";
import type { FeedbackSink } from "../../shared/feedback/index.ts";
import { ITEM } from "../ids.ts";
import type { PositionOf } from "./positions.ts";

/** The function that the page calls with each input. */
export type InputObserver = (ev: InputEvent) => void;

const NO_POSITION: PositionOf = () => undefined;

/** The flipper item of a flipper button, or null for another action. */
function flipperOf(action: InputEvent["action"]): string | null {
  if (action === "left_flipper") return ITEM.leftFlipper;
  if (action === "right_flipper") return ITEM.rightFlipper;
  return null;
}

/** The analog pull of an input, 0 to 1. An input with no value is a pull of 0. */
function pullOf(ev: InputEvent): number {
  return ev.value === undefined ? 0 : Math.min(1, Math.max(0, ev.value));
}

/** Make an observer. A repeated press or release of a held button gives no second event. */
export function createInputFeedback(
  sink: FeedbackSink,
  positionOf: PositionOf = NO_POSITION,
): InputObserver {
  const down = new Set<string>();

  const flipper = (item: string, pressed: boolean): void => {
    if (pressed === down.has(item)) return;
    if (pressed) down.add(item);
    else down.delete(item);
    const kind = pressed ? "flipper_down" : "flipper_up";
    sink.emit({ kind, element: item, pos: positionOf(item), strength: 1 });
  };

  const charge = (ev: InputEvent): void => {
    if (!ev.pressed) return;
    sink.emit({
      kind: "plunger_charge",
      element: ITEM.plunger,
      pos: positionOf(ITEM.plunger),
      strength: pullOf(ev),
    });
  };

  return (ev) => {
    const item = flipperOf(ev.action);
    if (item !== null) flipper(item, ev.pressed);
    else if (ev.action === "plunger") charge(ev);
  };
}
